import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Trash2, Plus } from 'lucide-react';

const LAMBDA = '\u03BB';
const EPSILON = '\u03B5';

function applyShortcuts(value) {
  return value.replace(/\\l/g, LAMBDA).replace(/\\e/g, EPSILON);
}

function RuleRow({ rule, index, onChange, onRemove, canRemove, onTabFromPush, autoFocusInput }) {
  const inputRef = useRef(null);
  const [singleCharWarning, setSingleCharWarning] = useState(null);

  useEffect(() => {
    if (autoFocusInput && inputRef.current) inputRef.current.focus();
  }, [autoFocusInput]);

  const handleField = (field, value) => {
    const processed = applyShortcuts(value);
    if ((field === 'input' || field === 'pop') && processed.length > 1) {
      setSingleCharWarning(field === 'input'
        ? 'Input reads a single character'
        : 'Pop removes a single stack symbol');
      return;
    }
    setSingleCharWarning(null);
    onChange(index, { ...rule, [field]: processed });
  };

  const handleKeyDown = (e, field) => {
    if (e.key === 'Tab' && !e.shiftKey && field === 'push') {
      e.preventDefault();
      onTabFromPush();
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      // Find and click the Done button
      e.target.closest('[data-pda-editor]')?.querySelector('[data-done-btn]')?.click();
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <input
        ref={inputRef}
        type="text"
        value={rule.input}
        onChange={(e) => handleField('input', e.target.value)}
        onKeyDown={(e) => handleKeyDown(e, 'input')}
        placeholder={LAMBDA}
        className="w-10 h-7 px-1.5 text-xs font-mono text-center bg-muted/50 border border-border/50 rounded focus:outline-none focus:ring-1 focus:ring-ring"
        data-testid={`pda-rule-input-${index}`}
      />
      <span className="text-muted-foreground text-xs">,</span>
      <input
        type="text"
        value={rule.pop}
        onChange={(e) => handleField('pop', e.target.value)}
        onKeyDown={(e) => handleKeyDown(e, 'pop')}
        placeholder={LAMBDA}
        className="w-10 h-7 px-1.5 text-xs font-mono text-center bg-muted/50 border border-border/50 rounded focus:outline-none focus:ring-1 focus:ring-ring"
        data-testid={`pda-rule-pop-${index}`}
      />
      <span className="text-muted-foreground text-xs">{'\u2192'}</span>
      <input
        type="text"
        value={rule.push}
        onChange={(e) => handleField('push', applyShortcuts(e.target.value))}
        onKeyDown={(e) => handleKeyDown(e, 'push')}
        placeholder={LAMBDA}
        className="w-16 h-7 px-1.5 text-xs font-mono text-center bg-muted/50 border border-border/50 rounded focus:outline-none focus:ring-1 focus:ring-ring"
        data-testid={`pda-rule-push-${index}`}
      />
      {canRemove && (
        <button
          onClick={() => onRemove(index)}
          className="p-0.5 hover:bg-destructive/10 rounded text-muted-foreground hover:text-destructive transition-colors"
          data-testid={`pda-rule-remove-${index}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
      {singleCharWarning && (
        <span className="text-[10px] text-amber-500 whitespace-nowrap">{singleCharWarning}</span>
      )}
    </div>
  );
}

export function PdaTransitionEditor({ transition, states, onSave, onCancel, onDelete, position }) {
  const fromState = states.find(s => s.id === transition.from_state);
  const toState = states.find(s => s.id === transition.to_state);
  const [rules, setRules] = useState(() => {
    const initial = transition.pda_rules && transition.pda_rules.length > 0
      ? transition.pda_rules.map(r => ({ input: r.input || '', pop: r.pop || '', push: r.push || '' }))
      : [{ input: '', pop: '', push: '' }];
    return initial;
  });
  const [focusNewRow, setFocusNewRow] = useState(false);
  const [initialFocus, setInitialFocus] = useState(true);
  const editorRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel]);

  // Auto-clear initial focus after mount
  useEffect(() => {
    if (initialFocus) {
      const timer = setTimeout(() => setInitialFocus(false), 100);
      return () => clearTimeout(timer);
    }
  }, [initialFocus]);

  // Click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (editorRef.current && !editorRef.current.contains(e.target)) {
        handleDone();
      }
    };
    setTimeout(() => window.addEventListener('mousedown', handler), 0);
    return () => window.removeEventListener('mousedown', handler);
  }, [rules]);

  const handleRuleChange = useCallback((index, newRule) => {
    setRules(prev => prev.map((r, i) => i === index ? newRule : r));
  }, []);

  const handleRemoveRule = useCallback((index) => {
    setRules(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.length === 0 ? [{ input: '', pop: '', push: '' }] : next;
    });
  }, []);

  const handleAddRule = useCallback(() => {
    setRules(prev => [...prev, { input: '', pop: '', push: '' }]);
    setFocusNewRow(true);
    setTimeout(() => setFocusNewRow(false), 100);
  }, []);

  const handleTabFromPush = useCallback(() => {
    handleAddRule();
  }, [handleAddRule]);

  const handleDone = useCallback(() => {
    // Filter out empty rules
    const validRules = rules.filter(r => r.input || r.pop || r.push);
    if (validRules.length === 0) {
      // All empty — delete the transition
      onDelete(transition.id);
      return;
    }
    onSave(transition.id, validRules);
  }, [rules, transition.id, onSave, onDelete]);

  return (
    <div
      ref={editorRef}
      data-pda-editor
      className="absolute z-50 bg-popover border border-border rounded-lg shadow-lg p-3 min-w-[260px]"
      style={{ left: position.x, top: position.y }}
      data-testid="pda-transition-editor"
    >
      <div className="text-xs text-muted-foreground mb-2 font-medium">
        {fromState?.name || '?'} {'\u2192'} {toState?.name || '?'}
      </div>

      {/* Column headers */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="w-10 text-[10px] text-muted-foreground text-center">Input</span>
        <span className="w-2" />
        <span className="w-10 text-[10px] text-muted-foreground text-center">Pop</span>
        <span className="w-4" />
        <span className="w-16 text-[10px] text-muted-foreground text-center">Push</span>
      </div>

      <div className="space-y-1.5">
        {rules.map((rule, i) => (
          <RuleRow
            key={i}
            rule={rule}
            index={i}
            onChange={handleRuleChange}
            onRemove={handleRemoveRule}
            canRemove={rules.length > 1}
            onTabFromPush={handleTabFromPush}
            autoFocusInput={(initialFocus && i === 0) || (focusNewRow && i === rules.length - 1)}
          />
        ))}
      </div>

      <button
        onClick={handleAddRule}
        className="flex items-center gap-1 mt-2 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
        data-testid="pda-add-rule-btn"
      >
        <Plus className="w-3 h-3" /> Add Rule
      </button>

      <div className="text-[10px] text-muted-foreground/60 mt-2 mb-2">
        Push: leftmost character becomes new top of stack
      </div>

      <div className="flex justify-end gap-1.5">
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" className="h-6 px-3 text-xs" onClick={handleDone} data-done-btn data-testid="pda-done-btn">
          Done
        </Button>
      </div>
    </div>
  );
}
